// src/encounter/plan-save-status.tsx
// [PROD-2949] The save state of a non-prescriber plan (allied-health Plan step and the nurse's Nursing
// plan), drawn INSIDE the plan's card (design desk, DESIGN-DESK CHANGES 73f5f42):
//   - `PlanSaveLine`   — "Saving plan…" / "✓ Plan saved" / a neutral notice, at the card header's
//                        right. NAMED (design desk D-66): the step header's own "Saved" is the NOTE's
//                        save, so the card says which save it means. Nothing while a notice speaks.
//   - `PlanSaveNotice` — a DS `Banner` at the top of the card (Banner, not Alert — confirmed by the
//                        desk: Alert is a 343x40 single-line toast that truncates):
//                          · "Not saved" (error, role="alert") ONLY for a save that was tried and
//                            failed, or that the server refused;
//                          · "Read only" (info) when the app already knows the role cannot write the
//                            plan — nothing was tried, so it is not a failure (D-66).
// The plan saves on its own path, apart from the note's autosave, so it shows its own state.
import { Banner, HelperText } from '@scripsteam/scrips-signal-ds';
import type { PlanSaveState } from './use-non-prescriber-plan-draft';

export function PlanSaveLine({ state }: { state?: PlanSaveState }) {
  if (!state) return null;
  return (
    <div aria-live="polite" data-testid="plan-save-line" className="min-h-4 text-right">
      {state.error || state.readOnlyReason ? null : state.saving ? (
        <p className="m-0 text-text-secondary text-size-xs">Saving plan…</p>
      ) : state.saved ? (
        <HelperText tone="success" className="m-0 text-size-xs">
          Plan saved
        </HelperText>
      ) : state.status ? (
        <p className="m-0 text-text-secondary text-size-xs">{state.status}</p>
      ) : null}
    </div>
  );
}

export function PlanSaveNotice({ state }: { state?: PlanSaveState }) {
  if (state?.error) {
    return (
      <Banner variant="error" title="Not saved" data-testid="plan-save-notice">
        {state.error}
      </Banner>
    );
  }
  if (state?.readOnlyReason) {
    return (
      <Banner variant="info" title="Read only" data-testid="plan-save-notice">
        {state.readOnlyReason}
      </Banner>
    );
  }
  return null;
}
